/**
 * Resource Alerts — React Query hooks.
 * Poll cadences follow the guide: summary 45s, list 30s, config on-load only,
 * detail on-open only. Mutations invalidate list + summary + detail.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buildQueryOpts, type PollOpts } from "@shared/hooks/buildQueryOpts";
import {
  getAlerts, getAlertDetail, getAlertSummary,
  acknowledgeAlert, acknowledgeBulk,
  getConfig, saveConfig, toggleConfig, resetConfig,
} from "../api/resourceAlerts.api";
import type { AlertsQuery, AlertType, Rule } from "../api/resourceAlerts.schemas";

export const raKeys = {
  all: ["resource-alerts"] as const,
  org: (o: string) => [...raKeys.all, o] as const,
  list: (o: string, q: AlertsQuery) => [...raKeys.org(o), "list", q] as const,
  detail: (o: string, id: string) => [...raKeys.org(o), "detail", id] as const,
  summary: (o: string) => [...raKeys.org(o), "summary"] as const,
  config: (o: string) => [...raKeys.org(o), "config"] as const,
};

export const useAlerts = (orgId: string, q: AlertsQuery = {}, opts: PollOpts = {}, enabled = true) =>
  useQuery(buildQueryOpts({
    key: raKeys.list(orgId, q),
    fn: () => getAlerts(orgId, q),
    enabled: orgId !== "" && enabled,
    staleMs: 15_000,
    defaultPollMs: 30_000,
    opts,
  }));

export const useAlertDetail = (orgId: string, alertId: string | null) =>
  useQuery(buildQueryOpts({
    key: raKeys.detail(orgId, alertId ?? "none"),
    fn: () => getAlertDetail(orgId, alertId as string),
    enabled: orgId !== "" && alertId !== null,
    staleMs: 15_000,
  }));

export const useAlertSummary = (orgId: string, opts: PollOpts = {}) =>
  useQuery(buildQueryOpts({
    key: raKeys.summary(orgId),
    fn: () => getAlertSummary(orgId),
    enabled: orgId !== "",
    staleMs: 30_000,
    defaultPollMs: 45_000,
    opts,
  }));

export const useConfig = (orgId: string) =>
  useQuery(buildQueryOpts({
    key: raKeys.config(orgId),
    fn: () => getConfig(orgId),
    enabled: orgId !== "",
    staleMs: 5 * 60_000,
  }));

/** Invalidate everything touched by an acknowledge (list + summary + detail). */
function useInvalidateAlerts(orgId: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({
    predicate: (query) => {
      const k = query.queryKey;
      return Array.isArray(k) && k[0] === "resource-alerts" && k[1] === orgId
        && (k[2] === "list" || k[2] === "summary" || k[2] === "detail");
    },
  });
}

export const useAcknowledge = (orgId: string) => {
  const invalidate = useInvalidateAlerts(orgId);
  return useMutation({
    mutationFn: (alertId: string) => acknowledgeAlert(orgId, alertId),
    onSuccess: () => { void invalidate(); },
  });
};

export const useAcknowledgeBulk = (orgId: string) => {
  const invalidate = useInvalidateAlerts(orgId);
  return useMutation({
    mutationFn: (alertIds: string[]) => acknowledgeBulk(orgId, alertIds),
    onSuccess: () => { void invalidate(); },
  });
};

/** Invalidate the config query after a write. */
function useInvalidateConfig(orgId: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: raKeys.config(orgId) });
}

export const useSaveConfig = (orgId: string) => {
  const invalidate = useInvalidateConfig(orgId);
  return useMutation({
    mutationFn: (rules: Record<string, Partial<Rule>>) => saveConfig(orgId, rules),
    onSuccess: () => { void invalidate(); },
  });
};

export const useToggleConfig = (orgId: string) => {
  const invalidate = useInvalidateConfig(orgId);
  return useMutation({
    mutationFn: (v: { alertType: AlertType; patch: Partial<Rule> }) =>
      toggleConfig(orgId, v.alertType, v.patch),
    onSuccess: () => { void invalidate(); },
  });
};

export const useResetConfig = (orgId: string) => {
  const invalidate = useInvalidateConfig(orgId);
  return useMutation({
    mutationFn: () => resetConfig(orgId),
    onSuccess: () => { void invalidate(); },
  });
};
